/**
 * Curve25519 scalar multiplication and the Ed25519 → Curve25519 birational map, pure-Kotlin and platform-independent.
 *
 * Ported faithfully from TweetNaCl `crypto_scalarmult` (the Montgomery ladder over GF(2²⁵⁵-19)) so the output is
 * byte-compatible with libsodium / tweetnacl-js. This is the asymmetric half of NaCl `box`; the symmetric half (HSalsa20
 * key-derivation + XSalsa20-Poly1305 `secretbox`) is reused from [SecretBox]. Keeping it here in commonMain continues the
 * Milestone-13 "approach 甲" decision (roadmap §20.3 / KTD4): the wrap-to-identity cipher lives **once**, so JVM and JS
 * never diverge and cross-platform interop is guaranteed by construction rather than reconciled by a shared test vector.
 *
 * The two [edToCurve...] functions implement `crypto_sign_ed25519_pk_to_curve25519` /
 * `crypto_sign_ed25519_sk_to_curve25519`: an Ed25519 signing identity *is* the encryption capability ([IdentityBox]),
 * so no second key is distributed.
 */
package com.oasisfeng.invaluable.identity

import org.kotlincrypto.hash.sha2.SHA512

internal object Curve25519 {

    const val SCALAR_SIZE = 32
    const val POINT_SIZE = 32

    /** `n · p` on Curve25519 (TweetNaCl `crypto_scalarmult`); [scalar] is clamped internally, [point] is the u-coord. */
    fun scalarMult(scalar: ByteArray, point: ByteArray): ByteArray {
        val z = ByteArray(32)
        for (i in 0 until 31) z[i] = scalar[i]
        z[31] = ((scalar[31].toInt() and 127) or 64).toByte()
        z[0] = (z[0].toInt() and 248).toByte()

        val x = unpack25519(point)
        val a = gf(); val b = gf(); val c = gf(); val d = gf(); val e = gf(); val f = gf()
        for (i in 0 until 16) b[i] = x[i]
        a[0] = 1; d[0] = 1
        for (i in 254 downTo 0) {
            val r = ((z[i ushr 3].toInt() ushr (i and 7)) and 1)
            sel25519(a, b, r); sel25519(c, d, r)
            add(e, a, c); sub(a, a, c); add(c, b, d); sub(b, b, d)
            sqr(d, e); sqr(f, a); mul(a, c, a); mul(c, b, e)
            add(e, a, c); sub(a, a, c); sqr(b, a); sub(c, d, f)
            mul(a, c, C121665); add(a, a, d); mul(c, c, a); mul(a, d, f)
            mul(d, b, x); sqr(b, e); sel25519(a, b, r); sel25519(c, d, r)
        }
        // Recover the affine x = X/Z: pack X · Z⁻¹.
        val zInv = gf(); for (i in 0 until 16) zInv[i] = c[i]
        inv25519(zInv, zInv)
        mul(a, a, zInv)
        return pack25519(a)
    }

    /** `n · basepoint` (TweetNaCl `crypto_scalarmult_base`): the public key for secret scalar [scalar]. */
    fun scalarMultBase(scalar: ByteArray): ByteArray = scalarMult(scalar, BASEPOINT)

    /**
     * `crypto_sign_ed25519_pk_to_curve25519`: Montgomery u = (1+y)/(1-y) of the Edwards y in [edPublicRaw] (32 raw
     * Ed25519 public-key bytes). Only y is needed, and [unpack25519] already masks the Edwards sign bit.
     */
    fun edToCurvePublic(edPublicRaw: ByteArray): ByteArray {
        val y = unpack25519(edPublicRaw)
        val a = gf(); val b = gf()
        add(a, GF1, y)          // a = 1 + y
        sub(b, GF1, y)          // b = 1 - y
        inv25519(b, b)
        mul(a, a, b)            // a = (1+y)/(1-y)
        return pack25519(a)
    }

    /** `crypto_sign_ed25519_sk_to_curve25519`: the clamped X25519 secret scalar = SHA-512(ed25519 seed)[0:32]. */
    fun edSeedToCurveSecret(edSeed: ByteArray): ByteArray {
        require(edSeed.size == 32) { "Ed25519 seed must be 32 bytes." }
        val h = SHA512().digest(edSeed)
        val s = h.copyOf(32)
        s[0] = (s[0].toInt() and 248).toByte()
        s[31] = ((s[31].toInt() and 127) or 64).toByte()
        return s
    }

    // --- GF(2²⁵⁵-19) field arithmetic (TweetNaCl `gf`, 16 little-endian 16-bit limbs in a LongArray) ---------------

    private val BASEPOINT = ByteArray(32).also { it[0] = 9 }
    private val C121665 = gf().also { it[0] = 0xDB41; it[1] = 1 }
    private val GF1 = gf().also { it[0] = 1 }

    private fun gf() = LongArray(16)

    private fun car25519(o: LongArray) {
        for (i in 0 until 16) {
            o[i] += (1L shl 16)
            val c = o[i] shr 16
            if (i < 15) o[i + 1] += c - 1 else o[0] += 38 * (c - 1)
            o[i] -= c shl 16
        }
    }

    private fun sel25519(p: LongArray, q: LongArray, b: Int) {
        val c = (b - 1).toLong().inv()
        for (i in 0 until 16) {
            val t = c and (p[i] xor q[i])
            p[i] = p[i] xor t
            q[i] = q[i] xor t
        }
    }

    private fun add(o: LongArray, a: LongArray, b: LongArray) { for (i in 0 until 16) o[i] = a[i] + b[i] }
    private fun sub(o: LongArray, a: LongArray, b: LongArray) { for (i in 0 until 16) o[i] = a[i] - b[i] }

    private fun mul(o: LongArray, a: LongArray, b: LongArray) {
        val t = LongArray(31)
        for (i in 0 until 16) for (j in 0 until 16) t[i + j] += a[i] * b[j]
        for (i in 0 until 15) t[i] += 38 * t[i + 16]
        for (i in 0 until 16) o[i] = t[i]
        car25519(o); car25519(o)
    }

    private fun sqr(o: LongArray, a: LongArray) = mul(o, a, a)

    private fun inv25519(o: LongArray, i: LongArray) {
        val c = gf(); for (a in 0 until 16) c[a] = i[a]
        for (a in 253 downTo 0) {
            sqr(c, c)
            if (a != 2 && a != 4) mul(c, c, i)
        }
        for (a in 0 until 16) o[a] = c[a]
    }

    private fun unpack25519(n: ByteArray): LongArray {
        val o = gf()
        for (i in 0 until 16) o[i] = (n[2 * i].toLong() and 0xFF) + ((n[2 * i + 1].toLong() and 0xFF) shl 8)
        o[15] = o[15] and 0x7fff
        return o
    }

    private fun pack25519(n: LongArray): ByteArray {
        val t = gf(); for (i in 0 until 16) t[i] = n[i]
        car25519(t); car25519(t); car25519(t)
        val m = gf()
        for (j in 0 until 2) {
            m[0] = t[0] - 0xffed
            for (i in 1 until 15) {
                m[i] = t[i] - 0xffff - ((m[i - 1] shr 16) and 1)
                m[i - 1] = m[i - 1] and 0xffff
            }
            m[15] = t[15] - 0x7fff - ((m[14] shr 16) and 1)
            val b = ((m[15] shr 16) and 1).toInt()
            m[14] = m[14] and 0xffff
            sel25519(t, m, 1 - b)
        }
        val o = ByteArray(32)
        for (i in 0 until 16) {
            o[2 * i] = (t[i] and 0xff).toByte()
            o[2 * i + 1] = ((t[i] shr 8) and 0xff).toByte()
        }
        return o
    }
}
